// src/components/ui/AvisoActualizacion.js
// Aviso de "hay versión nueva de la app" (deploy de hosting detectado vía /version.json).
// NUNCA recarga solo: muestra un modal con "Actualizar ahora" / "Seguir trabajando";
// si el usuario sigue trabajando, queda un botón flotante discreto (abajo-derecha)
// para actualizar cuando él decida. Montado una sola vez en Layout.js.
// El kiosko (fuera del Layout) tiene su propia lógica de auto-recarga en pantalla idle.
import React, { useState, useEffect, useRef } from 'react';
import { ArrowPathIcon, XMarkIcon } from './Icons';
import { obtenerVersionRemota, INTERVALO_CHEQUEO_MS } from '../../utils/versionApp';

// 🔴 05/10/2026 (caso SAC "los sets de placas no se agarran"): una pestaña de SAC siguió días con la versión
// vieja tocando "Seguir trabajando" y no conocía los sets de productos. Sin recargar NUNCA solo (se perdería
// un pedido a medias), el aviso insiste: vuelve a preguntar cada 4 horas y, pasado un día, dice el riesgo.
const REPETIR_AVISO_MS = 4 * 60 * 60 * 1000;
const VERSION_VIEJA_MS = 24 * 60 * 60 * 1000;

// `vistaPrevia` ({ fase, vieja }) solo sirve para mirarlo con `npm run ver-componente`; en el ERP nadie la pasa.
function AvisoActualizacion({ vistaPrevia } = {}) {
  // fase: 'nada' (sin novedades) | 'modal' (aviso inicial) | 'boton' (flotante persistente)
  const [fase, setFase] = useState((vistaPrevia && vistaPrevia.fase) || 'nada');
  const [vieja, setVieja] = useState(!!(vistaPrevia && vistaPrevia.vieja)); // más de un día desde que salió la versión nueva
  const faseRef = useRef('nada');
  const detectadoEnRef = useRef(0);
  const descartadoEnRef = useRef(0);
  const versionInicialRef = useRef(null);
  const detectadoRef = useRef(false);
  const ultimoChequeoRef = useRef(0);
  const cambiarFase = (f) => { faseRef.current = f; setFase(f); };

  useEffect(() => {
    let cancelado = false;

    // forzar=true saltea el throttle (p.ej. al restaurar la app desde bfcache: el
    // intervalo estuvo pausado en segundo plano y hay que chequear ya).
    const chequear = async (forzar = false) => {
      if (detectadoRef.current) return;
      if (!forzar && Date.now() - ultimoChequeoRef.current < 60000) return;
      ultimoChequeoRef.current = Date.now();
      const remota = await obtenerVersionRemota();
      if (cancelado || !remota) return;
      if (versionInicialRef.current === null) {
        // Primera lectura al cargar la pestaña = la versión con la que corre esta sesión
        versionInicialRef.current = remota;
        return;
      }
      if (remota !== versionInicialRef.current) {
        detectadoRef.current = true;
        detectadoEnRef.current = Date.now();
        cambiarFase('modal');
      }
    };

    // Si eligió "Seguir trabajando" y sigue con la versión vieja: cada 4 h vuelve el aviso, y pasado un día
    // el aviso y el botón dicen el riesgo. Nunca recarga solo.
    const recordar = () => {
      if (!detectadoRef.current || faseRef.current !== 'boton') return;
      const ahora = Date.now();
      if (ahora - detectadoEnRef.current >= VERSION_VIEJA_MS) setVieja(true);
      if (ahora - descartadoEnRef.current >= REPETIR_AVISO_MS) cambiarFase('modal');
    };

    chequear(true); // fija la versión base al montar
    const intervalo = setInterval(() => { chequear(true); recordar(); }, INTERVALO_CHEQUEO_MS);

    // Chequeo extra al volver a la pestaña/app (throttle 60s para no spamear)
    const alVolver = () => {
      if (document.visibilityState === 'visible') { chequear(); recordar(); }
    };
    document.addEventListener('visibilitychange', alVolver);
    window.addEventListener('focus', alVolver);
    window.addEventListener('online', alVolver);

    // En móvil (PWA) reabrir la app suele restaurar el documento desde bfcache:
    // 'visibilitychange' no siempre dispara, pero 'pageshow' con persisted=true sí.
    // Forzamos el chequeo para enterarnos del deploy nuevo apenas se reabre.
    const alMostrar = (e) => { if (e.persisted) chequear(true); };
    window.addEventListener('pageshow', alMostrar);

    return () => {
      cancelado = true;
      clearInterval(intervalo);
      document.removeEventListener('visibilitychange', alVolver);
      window.removeEventListener('focus', alVolver);
      window.removeEventListener('online', alVolver);
      window.removeEventListener('pageshow', alMostrar);
    };
  }, []);

  const actualizar = () => window.location.reload();
  const seguirTrabajando = () => { descartadoEnRef.current = Date.now(); cambiarFase('boton'); };

  if (fase === 'nada') return null;

  return (
    <>
      <style>{`
        @keyframes entradaAvisoActualizacion {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .aviso-actualizacion-flotante { bottom: 20px; }
        @media (max-width: 768px) {
          /* arriba de la bottom bar de accesos rápidos */
          .aviso-actualizacion-flotante { bottom: 78px; }
        }
      `}</style>

      {fase === 'modal' && (
        <div
          onMouseDown={(e) => { if (e.target === e.currentTarget) seguirTrabajando(); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10000, padding: '20px'
          }}
        >
          <div style={{
            background: 'var(--color-card)', borderRadius: '12px', padding: '28px',
            maxWidth: '440px', width: '100%', position: 'relative',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
            animation: 'entradaAvisoActualizacion 0.2s ease'
          }}>
            <button
              onClick={seguirTrabajando}
              aria-label="Cerrar"
              className="modal-close-std"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>

            <h3 style={{
              margin: '0 0 10px 0', color: 'var(--color-texto)',
              display: 'flex', alignItems: 'center', gap: '10px'
            }}>
              <span style={{ color: 'var(--color-primario)', display: 'inline-flex' }}>
                <ArrowPathIcon className="w-6 h-6" />
              </span>
              Nueva versión disponible
            </h3>
            <p style={{ margin: '0 0 6px 0', fontSize: '14px', color: 'var(--color-texto)', lineHeight: 1.5 }}>
              Hay una actualización del sistema lista. Podés actualizar ahora
              (la página se recarga en segundos) o seguir trabajando y actualizar
              cuando termines lo que estás haciendo.
            </p>
            {vieja && (
              <p style={{
                margin: '0 0 10px 0', padding: '8px 10px', fontSize: '13px', lineHeight: 1.5,
                background: 'var(--color-naranja-fondo)', color: 'var(--color-naranja-texto)',
                border: '1px solid var(--color-naranja)', borderRadius: '6px'
              }}>
                Esta pestaña lleva más de un día con la versión vieja. Puede que no aparezcan
                productos, sets o cambios nuevos (por ejemplo, al armar un pedido). Actualizá en cuanto
                termines lo que tenés abierto.
              </p>
            )}
            <p style={{ margin: '0 0 20px 0', fontSize: '12px', color: 'var(--color-texto-secundario)' }}>
              Si estás a media tarea (un pedido, un formulario), elegí "Seguir trabajando"
              para no perder lo escrito.
            </p>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                onClick={seguirTrabajando}
                className="btn-press hover-bright"
                style={{
                  height: '40px', padding: '0 18px', background: 'var(--color-neutro-solido)', color: 'white',
                  border: 'none', borderRadius: '6px', cursor: 'pointer',
                  fontSize: '14px', fontWeight: 600,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
                }}
              >
                Seguir trabajando
              </button>
              <button
                onClick={actualizar}
                className="btn-press hover-bright"
                style={{
                  height: '40px', padding: '0 18px', background: 'var(--color-primario)', color: 'white',
                  border: 'none', borderRadius: '6px', cursor: 'pointer',
                  fontSize: '14px', fontWeight: 600,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                }}
              >
                <ArrowPathIcon className="w-4 h-4" /> Actualizar ahora
              </button>
            </div>
          </div>
        </div>
      )}

      {fase === 'boton' && (
        <button
          onClick={actualizar}
          title={vieja
            ? 'Esta pestaña lleva más de un día con la versión vieja — click para actualizar'
            : 'Hay una nueva versión del sistema — click para actualizar'}
          className="aviso-actualizacion-flotante btn-press hover-bright"
          style={{
            position: 'fixed', right: '20px', zIndex: 9000,
            height: '40px', padding: '0 16px',
            background: vieja ? 'var(--color-naranja)' : 'var(--color-primario)', color: 'white', border: 'none',
            borderRadius: '20px', cursor: 'pointer',
            fontSize: '13px', fontWeight: 600,
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            boxShadow: '0 4px 14px rgba(102, 126, 234, 0.45)',
            animation: 'entradaAvisoActualizacion 0.25s ease'
          }}
        >
          <ArrowPathIcon className="w-4 h-4" /> {vieja ? 'Versión vieja: actualizar' : 'Actualizar app'}
        </button>
      )}
    </>
  );
}

export default AvisoActualizacion;
